/**
 * The table's five column labels, and how it is sorted.
 *
 * Every head is a button and every button cycles: ascending, descending, back to the DEFAULT, which
 * is the name ascending. `model/exerciseSort` owns that cycle — this file only says which glyph and
 * which word a column wears, and puts the state where assistive technology can read it.
 *
 * ── `aria-sort` on the columnheader, the caret on the button ────────────────────────────────
 * Three channels, and none of them alone: the active head is inked, it carries a caret pointing the
 * way it is sorted, and the header itself declares `ascending` / `descending` / `none`. An sr-only
 * tail on the button says what PRESSING it will do next, which is the thing a caret cannot convey —
 * the third press is the way back and nothing on screen would otherwise advertise it.
 *
 * ── Sortable heads are a deviation, and a recorded one ──────────────────────────────────────
 * `/plans` dropped its sort control by owner decision D1. This screen keeps one because the canon
 * draws it: a catalogue of four hundred movements is the case a sort is for, where a trainer's own
 * eight programmes are not. Entry 3 in `kanon-cwiczenia-2026-09-17/README.md`'s deviations register.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EXERCISE_SORT_COLUMNS,
  type ExerciseSort,
  type ExerciseSortColumn,
} from '../model/exerciseSort';

import {
  AuthorColumnGlyph,
  EquipmentColumnGlyph,
  ExerciseColumnGlyph,
  MusclesColumnGlyph,
  SortCaret,
  TagGlyph,
  TypeColumnGlyph,
} from './ExerciseLibraryIcons';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import styles from './ExerciseTable.module.css';

/**
 * One drawing per column, and the ORDER is `model/exerciseSort`'s.
 *
 * A `Record` rather than an array, so it is the five columns exactly: this file used to carry its
 * own list in its own order, which was `EXERCISE_SORT_COLUMNS` restated — two lists that nothing
 * checked against each other, and a column added to one and forgotten in the other would have been
 * a head that sorts by something the table cannot order, or an ordering with no head. The record's
 * key is `ExerciseSortColumn`, so a sixth column is a type error here rather than a silent absence.
 */
const GLYPHS: Readonly<
  Record<ExerciseSortColumn, (props: { readonly className?: string }) => React.ReactElement>
> = {
  name: ExerciseColumnGlyph,
  type: TypeColumnGlyph,
  muscles: MusclesColumnGlyph,
  equipment: EquipmentColumnGlyph,
  author: AuthorColumnGlyph,
};

/** TYP and AUTOR carry a mark rather than a text run, so their label is centred over it. */
const CENTRED: ReadonlySet<ExerciseSortColumn> = new Set<ExerciseSortColumn>(['type', 'author']);

interface ExerciseTableHeadProps {
  readonly sort: ExerciseSort;
  readonly onPressColumn: (column: ExerciseSortColumn) => void;
  /** Whether the „Tagi" column stands, between SPRZĘT and AUTOR. */
  readonly tagColumn: boolean;
}

export function ExerciseTableHead({ sort, onPressColumn, tagColumn }: ExerciseTableHeadProps) {
  const { t } = useTranslation();

  return (
    <div role="rowgroup" className={styles.headGroup}>
      {/* Row ONE of `aria-rowcount`: the data rows count from two, because only a window of them
        * is ever in the DOM and their own indexes are what say where they are. */}
      <div
        role="row"
        aria-rowindex={1}
        className={`${String(styles.gridRow)} ${String(styles.headRow)}`}
      >
        {EXERCISE_SORT_COLUMNS.map((column) => {
          const Glyph = GLYPHS[column];
          const active = sort.column === column;
          const descending = active && sort.descending;
          return (
            <Fragment key={column}>
            {column === 'author' && tagColumn && (
              // Not a sort: the canon draws the head plain, and a movement carries several tags,
              // so there is no one value a row could be ordered by.
              <span role="columnheader" className={styles.th}>
                <TagGlyph className={styles.thIcon} />
                {t('web:exerciseLibrary.columns.tags')}
              </span>
            )}
            <span
              role="columnheader"
              aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
              className={`${String(styles.th)} ${CENTRED.has(column) ? String(styles.thCentred) : ''}`}
            >
              <button
                type="button"
                className={`${String(styles.thButton)} ${active ? String(styles.thActive) : ''}`}
                onClick={() => {
                  onPressColumn(column);
                }}
              >
                <Glyph className={styles.thIcon} />
                {t(`web:exerciseLibrary.columns.${column}`)}
                {active && (
                  <SortCaret
                    className={`${String(styles.thCaret)} ${
                      descending ? '' : String(styles.thCaretUp)
                    }`}
                  />
                )}
                {/* What the NEXT press does, which is the half a caret cannot say — and the third
                  * press, the way back to the name, has no other advertisement. */}
                <span className={hidden.srOnly}>
                  {' '}
                  {t(
                    !active
                      ? 'web:exerciseLibrary.sort.ascending'
                      : descending
                        ? 'web:exerciseLibrary.sort.back'
                        : 'web:exerciseLibrary.sort.descending',
                  )}
                </span>
              </button>
            </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
